import { useState, useCallback, useRef } from "react";
import { useCreateDocumento, useUpdateDocumento } from "@/hooks/useDocumentos";
import {
  uploadPresignedFile,
  trainWithFileKey,
} from "@retro-ia/shared/upload";

// --- Types ---

type UploadStep =
  | "idle"
  | "creating"
  | "uploading"
  | "vectorizing"
  | "finalizing"
  | "success"
  | "error";

export interface UploadProgress {
  step: UploadStep;
  message: string;
  fileName: string;
}

export interface UseDocumentUploadOptions {
  idPrograma: number;
  cerebro: string;
  onSuccess?: (fileName: string) => void;
  onError?: (fileName: string, error: string) => void;
}

export interface UseDocumentUploadReturn {
  uploadFiles: (
    files: File[],
  ) => Promise<{ successCount: number; errorCount: number }>;
  progress: Map<string, UploadProgress>;
  isUploading: boolean;
  reset: () => void;
}

// --- Helpers ---

function getErrorMessage(error: unknown): string {
  if (!(error instanceof Error)) return "Error desconocido al procesar el archivo";
  if (error.message.includes("presignada") || error.message.includes("presigned")) {
    return "Error al obtener URL de subida. Verifica tu conexión.";
  }
  if (error.message.includes("train") || error.message.includes("vector")) {
    return "Error al procesar/vectorizar el archivo. El archivo se subió pero no se procesó.";
  }
  if (error.message.includes("network") || error.message.includes("Network")) {
    return "Error de conexión. Verifica tu internet.";
  }
  return error.message;
}

// --- Hook ---

export function useDocumentUpload({
  idPrograma,
  cerebro,
  onSuccess,
  onError,
}: UseDocumentUploadOptions): UseDocumentUploadReturn {
  const [progress, setProgress] = useState<Map<string, UploadProgress>>(
    new Map(),
  );
  const [isUploading, setIsUploading] = useState(false);
  const abortRef = useRef(false);

  const { mutateAsync: createDocumentoAsync } = useCreateDocumento();
  const { mutateAsync: updateDocumentoAsync } = useUpdateDocumento();

  const updateFileProgress = useCallback(
    (fileName: string, step: UploadStep, message: string) => {
      setProgress((prev) => {
        const next = new Map(prev);
        next.set(fileName, { step, message, fileName });
        return next;
      });
    },
    [],
  );

  const processFile = useCallback(
    async (file: File): Promise<boolean> => {
      let createdDocId: number | null = null;

      try {
        // 1. Crear documento en BD (cargado: false)
        updateFileProgress(file.name, "creating", "Creando documento...");
        const created = await createDocumentoAsync({
          idPrograma,
          archivo: { llave: "", nombre: file.name },
          cargado: false,
          mensaje: "Procesando...",
        });
        createdDocId = created.id;

        // 2. Upload a S3 via presigned URL
        updateFileProgress(file.name, "uploading", "Subiendo archivo...");
        const presigned = await uploadPresignedFile({
          file,
          ruta: `private/${cerebro}/documentos/${file.name}`,
          fileName: file.name,
          fileType: file.type,
          isPublic: false,
        });

        if (!presigned?.llave) {
          throw new Error("Error al obtener URL presignada");
        }

        // 3. Actualizar documento con llave, url y tamaño
        await updateDocumentoAsync({
          id: created.id,
          archivo: {
            llave: presigned.llave,
            nombre: file.name,
            url: presigned.url ?? undefined,
            size: file.size,
          },
        });

        // 4. Vectorizar
        updateFileProgress(file.name, "vectorizing", "Vectorizando archivo...");
        await trainWithFileKey({
          key: presigned.llave,
          brain: cerebro,
          documentId: created.id,
          fileName: file.name,
        });

        // 5. Marcar como cargado
        updateFileProgress(file.name, "finalizing", "Finalizando...");
        await updateDocumentoAsync({
          id: created.id,
          cargado: true,
          mensaje: "Archivo cargado correctamente",
        });

        updateFileProgress(file.name, "success", "Archivo cargado correctamente");
        onSuccess?.(file.name);
        return true;
      } catch (error) {
        const errorMessage = getErrorMessage(error);

        // Si ya creamos el documento, actualizarlo con el error
        if (createdDocId != null) {
          try {
            await updateDocumentoAsync({
              id: createdDocId,
              cargado: true,
              mensaje: errorMessage,
            });
          } catch (updateErr) {
            console.error("Error al actualizar documento con error:", updateErr);
          }
        }

        updateFileProgress(file.name, "error", errorMessage);
        onError?.(file.name, errorMessage);
        return false;
      }
    },
    [
      idPrograma,
      cerebro,
      createDocumentoAsync,
      updateDocumentoAsync,
      updateFileProgress,
      onSuccess,
      onError,
    ],
  );

  const uploadFiles = useCallback(
    async (files: File[]) => {
      setIsUploading(true);
      abortRef.current = false;
      let successCount = 0;
      let errorCount = 0;

      for (const file of files) {
        if (abortRef.current) break;
        const ok = await processFile(file);
        if (ok) successCount++;
        else errorCount++;
      }

      setIsUploading(false);
      return { successCount, errorCount };
    },
    [processFile],
  );

  const reset = useCallback(() => {
    abortRef.current = true;
    setProgress(new Map());
    setIsUploading(false);
  }, []);

  return { uploadFiles, progress, isUploading, reset };
}
